import React17, { isValidElement, Children, Fragment } from 'react';
import { styled, keyframes } from '@atom-web/core';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as LabelPrimitive from '@radix-ui/react-label';
import merge from 'lodash.merge';
import CloseCircle from '@atom-web/icons-16/CloseCircle';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import Checkmark from '@atom-web/icons-16/Checkmark';
import ChevronDown from '@atom-web/icons-16/ChevronDown';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var CoreBox = styled("div", {
  boxSizing: "border-box"
});
var Box = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreBox, {
    ...props,
    ref: forwardedRef
  });
});
Box.displayName = "Box";
var Box_default = Box;
var StyledFlex = styled("div", {
  boxSizing: "border-box",
  display: "flex",
  variants: {
    direction: {
      row: {
        flexDirection: "row"
      },
      column: {
        flexDirection: "column"
      },
      rowReverse: {
        flexDirection: "row-reverse"
      },
      columnReverse: {
        flexDirection: "column-reverse"
      }
    },
    align: {
      start: {
        alignItems: "flex-start"
      },
      center: {
        alignItems: "center"
      },
      end: {
        alignItems: "flex-end"
      },
      stretch: {
        alignItems: "stretch"
      },
      baseline: {
        alignItems: "baseline"
      }
    },
    justify: {
      start: {
        justifyContent: "flex-start"
      },
      center: {
        justifyContent: "center"
      },
      end: {
        justifyContent: "flex-end"
      },
      between: {
        justifyContent: "space-between"
      }
    },
    wrap: {
      noWrap: {
        flexWrap: "nowrap"
      },
      wrap: {
        flexWrap: "wrap"
      },
      wrapReverse: {
        flexWrap: "wrap-reverse"
      }
    },
    gap: {
      1: {
        gap: "$1"
      },
      2: {
        gap: "$2"
      },
      3: {
        gap: "$3"
      },
      4: {
        gap: "$4"
      },
      5: {
        gap: "$5"
      },
      6: {
        gap: "$6"
      },
      7: {
        gap: "$7"
      },
      8: {
        gap: "$8"
      },
      9: {
        gap: "$9"
      }
    }
  },
  defaultVariants: {
    direction: "row",
    align: "stretch",
    justify: "start",
    wrap: "noWrap"
  }
});
var FlexBox = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(StyledFlex, {
    ...props,
    ref: forwardedRef
  });
});
FlexBox.displayName = "FlexBox";
var Flex_default = FlexBox;
var Column = React17.forwardRef(({ reverse, ...rest }, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(StyledFlex, {
    direction: reverse ? "columnReverse" : "column",
    ...rest,
    ref: forwardedRef
  });
});
Column.displayName = "Column";
var Column_default = Column;
var Row = React17.forwardRef(({ reverse, ...rest }, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(StyledFlex, {
    direction: reverse ? "rowReverse" : "row",
    ...rest,
    ref: forwardedRef
  });
});
Row.displayName = "Row";
var Row_default = Row;
var StyledGrid = styled("div", {
  boxSizing: "border-box",
  display: "grid",
  variants: {
    align: {
      start: {
        alignItems: "start"
      },
      center: {
        alignItems: "center"
      },
      end: {
        alignItems: "end"
      },
      stretch: {
        alignItems: "stretch"
      },
      baseline: {
        alignItems: "baseline"
      }
    },
    justify: {
      start: {
        justifyContent: "start"
      },
      center: {
        justifyContent: "center"
      },
      end: {
        justifyContent: "end"
      },
      between: {
        justifyContent: "space-between"
      }
    },
    flow: {
      row: {
        gridAutoFlow: "row"
      },
      column: {
        gridAutoFlow: "column"
      },
      dense: {
        gridAutoFlow: "dense"
      },
      rowDense: {
        gridAutoFlow: "row dense"
      },
      columnDense: {
        gridAutoFlow: "column dense"
      }
    },
    columns: {
      1: {
        gridTemplateColumns: "repeat(1, 1fr)"
      },
      2: {
        gridTemplateColumns: "repeat(2, 1fr)"
      },
      3: {
        gridTemplateColumns: "repeat(3, 1fr)"
      },
      4: {
        gridTemplateColumns: "repeat(4, 1fr)"
      }
    },
    gap: {
      1: {
        gap: "$1"
      },
      2: {
        gap: "$2"
      },
      3: {
        gap: "$3"
      },
      4: {
        gap: "$4"
      },
      5: {
        gap: "$5"
      },
      6: {
        gap: "$6"
      },
      7: {
        gap: "$7"
      },
      8: {
        gap: "$8"
      },
      9: {
        gap: "$9"
      }
    },
    gapX: {
      1: {
        columnGap: "$1"
      },
      2: {
        columnGap: "$2"
      },
      3: {
        columnGap: "$3"
      },
      4: {
        columnGap: "$4"
      },
      5: {
        columnGap: "$5"
      },
      6: {
        columnGap: "$6"
      },
      7: {
        columnGap: "$7"
      },
      8: {
        columnGap: "$8"
      },
      9: {
        columnGap: "$9"
      }
    },
    gapY: {
      1: {
        rowGap: "$1"
      },
      2: {
        rowGap: "$2"
      },
      3: {
        rowGap: "$3"
      },
      4: {
        rowGap: "$4"
      },
      5: {
        rowGap: "$5"
      },
      6: {
        rowGap: "$6"
      },
      7: {
        rowGap: "$7"
      },
      8: {
        rowGap: "$8"
      },
      9: {
        rowGap: "$9"
      }
    }
  }
});
var Grid = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(StyledGrid, {
    ...props,
    ref: forwardedRef
  });
});
Grid.displayName = "Grid";
var Grid_default = Grid;
var CoreStack = styled(CoreBox, {
  display: "flex",
  flexWrap: "wrap",
  flexDirection: "column",
  variants: {
    gap: {
      1: {
        gap: "$1"
      },
      2: {
        gap: "$2"
      },
      3: {
        gap: "$3"
      },
      4: {
        gap: "$4"
      },
      5: {
        gap: "$5"
      },
      6: {
        gap: "$6"
      },
      7: {
        gap: "$7"
      },
      8: {
        gap: "$8"
      },
      9: {
        gap: "$9"
      }
    }
  },
  defaultVariants: {
    gap: "2"
  }
});
var Stack = ({ css, children, seperator, ...rest }) => {
  let lg = {
    flexDirection: "row"
  };
  if (css && css["@lg"]) {
    lg = {
      ...lg,
      ...css["@lg"]
    };
  }
  const StyledStack = styled(CoreStack, {
    ...css,
    "@lg": lg
  });
  if (isValidElement(seperator)) {
    return /* @__PURE__ */ React17.createElement(StyledStack, {
      ...rest
    }, Children.toArray(children).map((child, index, self) => {
      return /* @__PURE__ */ React17.createElement(Fragment, {
        key: "child" + index
      }, child, seperator && index !== self.length - 1 && seperator);
    }));
  }
  return /* @__PURE__ */ React17.createElement(StyledStack, {
    ...rest
  }, children);
};
var Stack_default = Stack;
var StyledSeparator = styled(SeparatorPrimitive.Root, {
  border: "none",
  margin: 0,
  flexShrink: 0,
  backgroundColor: "$divider-bg",
  cursor: "default",
  variants: {
    size: {
      full: {
        '&[data-orientation="horizontal"]': {
          height: "1px",
          width: "100%"
        },
        '&[data-orientation="vertical"]': {
          width: "1px",
          height: "100%"
        }
      },
      "1": {
        '&[data-orientation="horizontal"]': {
          height: "1px",
          width: "$3"
        },
        '&[data-orientation="vertical"]': {
          width: "1px",
          height: "$3"
        }
      },
      "2": {
        '&[data-orientation="horizontal"]': {
          height: "1px",
          width: "$7"
        },
        '&[data-orientation="vertical"]': {
          width: "1px",
          height: "$7"
        }
      }
    }
  },
  defaultVariants: {
    size: "full"
  }
});
var Divider = (props) => {
  return /* @__PURE__ */ React17.createElement(StyledSeparator, {
    ...props
  });
};
var Divider_default = Divider;
var pulse = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: "100%" }
});
var CoreSkeleton = styled("div", {
  backgroundColor: "$skeleton-bg",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    animationName: `${pulse}`,
    animationDuration: "500ms",
    animationDirection: "alternate",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    backgroundColor: "$skeleton-after-bg",
    borderRadius: "inherit",
    bottom: 0,
    content: '""',
    left: 0,
    position: "absolute",
    right: 0,
    top: 0
  },
  variants: {
    variant: {
      avatar1: {
        borderRadius: "$round",
        height: "$3",
        width: "$3"
      },
      avatar2: {
        borderRadius: "$round",
        height: "$5",
        width: "$5"
      },
      avatar3: {
        borderRadius: "$round",
        height: "$6",
        width: "$6"
      },
      avatar4: {
        borderRadius: "$round",
        height: "$7",
        width: "$7"
      },
      avatar5: {
        borderRadius: "$round",
        height: "$8",
        width: "$8"
      },
      avatar6: {
        borderRadius: "$round",
        height: "$9",
        width: "$9"
      },
      text: {
        height: "$1"
      },
      title: {
        height: "$5"
      },
      heading: {
        height: "$3"
      },
      button: {
        borderRadius: "$1",
        height: "$5",
        width: "$8"
      }
    }
  },
  defaultVariants: {
    variant: "text"
  }
});
var Skeleton = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreSkeleton, {
    ...props,
    ref: forwardedRef
  });
});
var Skeleton_default = Skeleton;
var StyledIconButton = styled("button", {
  alignItems: "center",
  appearance: "none",
  borderWidth: "0",
  boxSizing: "border-box",
  display: "inline-flex",
  flexShrink: 0,
  fontFamily: "inherit",
  fontSize: "14px",
  justifyContent: "center",
  lineHeight: "1",
  outline: "none",
  padding: "0",
  textDecoration: "none",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
  color: "$icon-btn",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  backgroundColor: "$icon-btn-bg",
  border: "1px solid $icon-btn-border",
  "@hover": {
    "&:hover": {
      borderColor: "$icon-btn-hover-border"
    }
  },
  "&:active": {
    backgroundColor: "$icon-btn-active-bg"
  },
  "&:focus-visible": {
    borderColor: "$icon-btn-focus-border",
    boxShadow: "0 0 0 1px $colors$icon-btn-focus-shadow"
  },
  "&:disabled": {
    pointerEvents: "none",
    backgroundColor: "transparent",
    color: "$icon-btn-disabled"
  },
  variants: {
    size: {
      "1": {
        borderRadius: "$1",
        height: "$5",
        width: "$5"
      },
      "2": {
        borderRadius: "$2",
        height: "$6",
        width: "$6"
      },
      "3": {
        borderRadius: "$2",
        height: "$7",
        width: "$7"
      },
      "4": {
        borderRadius: "$3",
        height: "$8",
        width: "$8"
      }
    },
    variant: {
      ghost: {
        backgroundColor: "transparent",
        borderWidth: "0",
        "@hover": {
          "&:hover": {
            backgroundColor: "$icon-btn-ghost-hover-bg"
          }
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$icon-btn-ghost-focus-shadow, 0 0 0 1px $colors$icon-btn-ghost-focus-shadow"
        },
        "&:active": {
          backgroundColor: "$icon-btn-ghost-active-bg"
        }
      },
      raised: {
        boxShadow: "0 0 transparent, 0 16px 32px hsl(206deg 12% 5% / 25%), 0 3px 5px hsl(0deg 0% 0% / 10%)",
        "@hover": {
          "&:hover": {
            boxShadow: "0 0 transparent, 0 16px 32px hsl(206deg 12% 5% / 25%), 0 3px 5px hsl(0deg 0% 0% / 10%)"
          }
        },
        "&:focus-visible": {
          borderColor: "$icon-btn-raised-focus-border",
          boxShadow: "0 0 0 1px $colors$icon-btn-raised-focus-shadow, 0 16px 32px hsl(206deg 12% 5% / 25%), 0 3px 5px hsl(0deg 0% 0% / 10%)"
        },
        "&:active": {
          backgroundColor: "$icon-btn-raised-active-bg"
        }
      }
    },
    state: {
      active: {
        backgroundColor: "$icon-btn-active-bg",
        boxShadow: "inset 0 0 0 1px hsl(206,10%,76%)",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px hsl(206,10%,76%)"
          }
        },
        "&:active": {
          backgroundColor: "$icon-btn-active-bg"
        }
      },
      waiting: {
        backgroundColor: "$icon-btn-waiting-bg",
        boxShadow: "inset 0 0 0 1px hsl(206,10%,76%)",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px hsl(206,10%,76%)"
          }
        },
        "&:active": {
          backgroundColor: "$icon-btn-waiting-bg"
        }
      }
    }
  },
  defaultVariants: {
    size: "1",
    variant: "ghost"
  }
});
var IconButton = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(StyledIconButton, {
    ...props,
    ref: forwardedRef
  });
});
IconButton.displayName = "IconButton";
var IconButton_default = IconButton;
var CoreButton = styled("button", {
  all: "unset",
  alignItems: "center",
  boxSizing: "border-box",
  userSelect: "none",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  display: "inline-flex",
  flexShrink: 0,
  justifyContent: "center",
  lineHeight: "1",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  cursor: "pointer",
  height: "$5",
  paddingLeft: "$2",
  paddingRight: "$2",
  fontFamily: "$btn-font",
  fontSize: "$2",
  fontWeight: "$normal",
  fontVariantNumeric: "tabular-nums",
  "&:disabled": {
    backgroundColor: "$btn-disabled-bg",
    boxShadow: "inset 0 0 0 1px $colors$btn-disabled-shadow",
    color: "$btn-disabled",
    pointerEvents: "none",
    cursor: "not-allowed"
  },
  variants: {
    size: {
      sm: {
        borderRadius: "$1",
        height: "$5",
        paddingLeft: "$2",
        paddingRight: "$2",
        fontSize: "$1",
        lineHeight: "$sizes$5"
      },
      md: {
        borderRadius: "$2",
        height: "$6",
        paddingLeft: "$3",
        paddingRight: "$3",
        fontSize: "$3",
        lineHeight: "$sizes$6"
      },
      lg: {
        borderRadius: "$2",
        height: "$7",
        paddingLeft: "$4",
        paddingRight: "$4",
        fontSize: "$4",
        lineHeight: "$sizes$7"
      },
      xl: {
        borderRadius: "$3",
        height: "$8",
        paddingLeft: "$6",
        paddingRight: "$6",
        fontSize: "$5",
        lineHeight: "$sizes$7"
      }
    },
    wide: {
      md: {},
      lg: {}
    },
    weight: {
      hairline: {
        fontWeight: "$hairline"
      },
      thin: {
        fontWeight: "$thin"
      },
      light: {
        fontWeight: "$light"
      },
      normal: {
        fontWeight: "$normal"
      },
      medium: {
        fontWeight: "$medium"
      },
      semibold: {
        fontWeight: "$semibold"
      },
      bold: {
        fontWeight: "$bold"
      },
      extrabold: {
        fontWeight: "$extrabold"
      },
      black: {
        fontWeight: "$black"
      }
    },
    primary: {
      true: {
        color: "$btn-primary",
        backgroundColor: "$btn-primary-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-primary-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px $colors$btn-primary-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-primary-active",
          backgroundColor: "$btn-primary-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-primary-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-primary-focus-shadow, 0 0 0 1px $colors$btn-primary-focus-shadow"
        }
      }
    },
    secondary: {
      true: {
        color: "$btn-secondary",
        backgroundColor: "$btn-secondary-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-secondary-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px $colors$btn-secondary-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-secondary-active",
          backgroundColor: "$btn-secondary-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-secondary-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-secondary-focus-shadow, 0 0 0 1px $colors$btn-secondary-focus-shadow"
        }
      }
    },
    danger: {
      true: {
        color: "$btn-danger",
        backgroundColor: "$btn-danger-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-danger-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px $colors$btn-danger-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-danger-active",
          backgroundColor: "$btn-danger-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-danger-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-danger-focus-shadow, 0 0 0 1px $colors$btn-danger-focus-shadow"
        }
      }
    },
    outlined: {
      true: {
        color: "$btn-outlined",
        backgroundColor: "$btn-outlined-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-outlined-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px $colors$btn-outlined-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-outlined-active",
          backgroundColor: "$btn-outlined-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-outlined-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-outlined-focus-shadow, 0 0 0 1px $colors$btn-outlined-focus-shadow"
        }
      }
    },
    gradient: {
      true: {
        "&:disabled": {
          background: "$btn-disabled-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-disabled-shadow",
          color: "$btn-disabled",
          pointerEvents: "none",
          cursor: "not-allowed"
        }
      }
    },
    state: {
      active: {},
      loading: {
        backgroundColor: "$btn-disabled-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-disabled-shadow",
        color: "$btn-disabled",
        cursor: "progress",
        "@hover": {
          "&:hover": {
            backgroundColor: "$btn-disabled-bg",
            boxShadow: "inset 0 0 0 1px $colors$btn-disabled-shadow",
            color: "$btn-disabled"
          }
        },
        "&:active": {
          backgroundColor: "$btn-disabled-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-disabled-shadow",
          color: "$btn-disabled"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-disabled-shadow, 0 0 0 1px $colors$btn-disabled-shadow"
        }
      }
    }
  },
  compoundVariants: [
    {
      outlined: true,
      primary: true,
      css: {
        color: "$btn-outlined-primary",
        backgroundColor: "$btn-outlined-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-primary-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1.5px $colors$btn-primary-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-primary-active",
          backgroundColor: "$btn-primary-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-primary-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-primary-focus-shadow, 0 0 0 1px $colors$btn-primary-focus-shadow"
        }
      }
    },
    {
      outlined: true,
      secondary: true,
      css: {
        color: "$btn-outlined-secondary",
        backgroundColor: "$btn-outlined-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-secondary-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1.5px $colors$btn-secondary-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-secondary-active",
          backgroundColor: "$btn-secondary-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-secondary-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-secondary-focus-shadow, 0 0 0 1px $colors$btn-secondary-focus-shadow"
        }
      }
    },
    {
      outlined: true,
      danger: true,
      css: {
        color: "$btn-outlined-danger",
        backgroundColor: "$btn-outlined-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-danger-shadow",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1.5px $colors$btn-danger-hover-shadow"
          }
        },
        "&:active": {
          color: "$btn-danger-active",
          backgroundColor: "$btn-danger-active-bg",
          boxShadow: "inset 0 0 0 1px $colors$btn-danger-active-shadow"
        },
        "&:focus-visible": {
          boxShadow: "inset 0 0 0 1px $colors$btn-danger-focus-shadow, 0 0 0 1px $colors$btn-danger-focus-shadow"
        }
      }
    },
    {
      state: "active",
      primary: true,
      css: {
        color: "$btn-primary-active",
        backgroundColor: "$btn-primary-active-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-primary-active-shadow"
      }
    },
    {
      state: "active",
      secondary: true,
      css: {
        color: "$btn-secondary-active",
        backgroundColor: "$btn-secondary-active-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-secondary-active-shadow"
      }
    },
    {
      state: "active",
      danger: true,
      css: {
        color: "$btn-danger-active",
        backgroundColor: "$btn-danger-active-bg",
        boxShadow: "inset 0 0 0 1px $colors$btn-danger-active-shadow"
      }
    },
    {
      primary: true,
      gradient: true,
      css: {
        color: "$btn-primary-gradient",
        background: "$colors$btn-primary-gradient-bg",
        boxShadow: "none",
        "&:active": {
          color: "$btn-primary-gradient-active",
          background: "$colors$btn-primary-gradient-active-bg"
        }
      }
    },
    {
      secondary: true,
      gradient: true,
      css: {
        color: "$btn-secondary-gradient",
        background: "$colors$btn-secondary-gradient-bg",
        boxShadow: "none",
        "&:active": {
          color: "$btn-secondary-gradient-active",
          background: "$colors$btn-secondary-gradient-active-bg"
        }
      }
    },
    {
      primary: true,
      gradient: true,
      state: "active",
      css: {
        color: "$btn-primary-gradient-active",
        background: "$colors$btn-primary-gradient-active-bg"
      }
    },
    {
      secondary: true,
      gradient: true,
      state: "active",
      css: {
        color: "$btn-secondary-gradient-active",
        background: "$colors$btn-secondary-gradient-active-bg"
      }
    },
    {
      size: "sm",
      wide: "md",
      css: {
        paddingLeft: "$3",
        paddingRight: "$3"
      }
    },
    {
      size: "sm",
      wide: "lg",
      css: {
        paddingLeft: "$4",
        paddingRight: "$4"
      }
    },
    {
      size: "md",
      wide: "md",
      css: {
        paddingLeft: "$4",
        paddingRight: "$4"
      }
    },
    {
      size: "md",
      wide: "lg",
      css: {
        paddingLeft: "$5",
        paddingRight: "$5"
      }
    },
    {
      size: "lg",
      wide: "md",
      css: {
        paddingLeft: "$5",
        paddingRight: "$5"
      }
    },
    {
      size: "lg",
      wide: "lg",
      css: {
        paddingLeft: "$6",
        paddingRight: "$6"
      }
    },
    {
      size: "xl",
      wide: "md",
      css: {
        paddingLeft: "$7",
        paddingRight: "$7"
      }
    },
    {
      size: "xl",
      wide: "lg",
      css: {
        paddingLeft: "$8",
        paddingRight: "$8"
      }
    }
  ],
  defaultVariants: {
    size: "md",
    primary: true
  }
});
var Button = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreButton, {
    ...props,
    ref: forwardedRef
  });
});
Button.displayName = "Button";
var Button_default = Button;
var CoreText = styled("span", {
  lineHeight: "1",
  margin: "0",
  fontFamily: "$text",
  fontWeight: 400,
  fontVariantNumeric: "tabular-nums",
  display: "block",
  variants: {
    size: {
      "1": {
        fontSize: "$1"
      },
      "2": {
        fontSize: "$2"
      },
      "3": {
        fontSize: "$3"
      },
      "4": {
        fontSize: "$4"
      },
      "5": {
        fontSize: "$5",
        letterSpacing: "-.015em"
      },
      "6": {
        fontSize: "$6",
        letterSpacing: "-.016em"
      },
      "7": {
        fontSize: "$7",
        letterSpacing: "-.031em",
        textIndent: "-.005em"
      },
      "8": {
        fontSize: "$8",
        letterSpacing: "-.034em",
        textIndent: "-.018em"
      },
      "9": {
        fontSize: "$9",
        letterSpacing: "-.055em",
        textIndent: "-.025em"
      }
    }
  },
  defaultVariants: {
    size: "3"
  }
});
var Text = React17.forwardRef((props, forwardedRef) => /* @__PURE__ */ React17.createElement(CoreText, {
  ...props,
  ref: forwardedRef
}));
Text.displayName = "Text";
var Text_default = Text;
var CoreLabel = styled(LabelPrimitive.Root, CoreText, {
  display: "inline-block",
  verticalAlign: "middle",
  cursor: "default"
});
var Label = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreLabel, {
    as: "label",
    ...props,
    ref: forwardedRef
  });
});
Label.displayName = "Label";
var Label_default = Label;
var CoreTextArea = styled("textarea", {
  appearance: "none",
  borderWidth: "0",
  fontFamily: "inherit",
  margin: "0",
  outline: "none",
  padding: "$1",
  width: "100%",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  backgroundColor: "$textarea-bg",
  boxShadow: "inset 0 0 0 1px $colors$textarea-shadow",
  color: "$textarea",
  fontVariantNumeric: "tabular-nums",
  position: "relative",
  minHeight: 80,
  resize: "vertical",
  "&:focus": {
    boxShadow: "inset 0px 0px 0px 1px $colors$textarea-focus-shadow, 0px 0px 0px 1px $colors$textarea-focus-shadow",
    zIndex: "1"
  },
  "&::placeholder": {
    color: "$textarea-placeholder"
  },
  "&:disabled": {
    pointerEvents: "none",
    backgroundColor: "$textarea-disabled-bg",
    color: "$textarea-disabled",
    cursor: "not-allowed",
    resize: "none",
    "&::placeholder": {
      color: "$textarea-disabled-placeholder"
    }
  },
  "&:read-only": {
    backgroundColor: "$textarea-read-only-bg",
    "&:focus": {
      boxShadow: "inset 0px 0px 0px 1px $colors$textarea-read-only-foucs-shadow"
    }
  },
  variants: {
    size: {
      "1": {
        borderRadius: "$1",
        fontSize: "$1",
        lineHeight: "16px",
        paddingLeft: "$1",
        paddingRight: "$1"
      },
      "2": {
        borderRadius: "$1",
        fontSize: "$2",
        lineHeight: "20px",
        paddingLeft: "$1",
        paddingRight: "$1"
      },
      "3": {
        borderRadius: "$2",
        fontSize: "$3",
        lineHeight: "23px",
        paddingLeft: "$2",
        paddingRight: "$2"
      }
    },
    state: {
      invalid: {
        boxShadow: "inset 0 0 0 1px $colors$textarea-invalid-shadow",
        "&:focus": {
          boxShadow: "inset 0px 0px 0px 1px $colors$textarea-invalid-focus-shadow, 0px 0px 0px 1px $colors$textarea-invalid-focus-shadow"
        }
      },
      valid: {
        boxShadow: "inset 0 0 0 1px $colors$textarea-valid-shadow",
        "&:focus": {
          boxShadow: "inset 0px 0px 0px 1px $colors$textarea-valid-focus-shadow, 0px 0px 0px 1px $colors$textarea-valid-focus-shadow"
        }
      }
    },
    cursor: {
      default: {
        cursor: "default",
        "&:focus": {
          cursor: "text"
        }
      },
      text: {
        cursor: "text"
      }
    }
  },
  defaultVariants: {
    size: "1"
  }
});
var TextArea = React17.forwardRef((props, forwardedRef) => /* @__PURE__ */ React17.createElement(CoreTextArea, {
  ...props,
  ref: forwardedRef
}));
TextArea.displayName = "TextArea";
var TextArea_default = TextArea;
var DEFAULT_TAG = "p";
var Paragraph = React17.forwardRef((props, forwardedRef) => {
  const { size = "1", ...textProps } = props;
  const textSize = {
    1: { "@initial": "3", "@lg": "4" },
    2: { "@initial": "5", "@lg": "6" }
  };
  const textCss = {
    1: { lineHeight: "25px", "@lg": { lineHeight: "27px" } },
    2: {
      lineHeight: "27px",
      "@lg": { lineHeight: "30px" }
    }
  };
  return /* @__PURE__ */ React17.createElement(CoreText, {
    as: DEFAULT_TAG,
    ...textProps,
    ref: forwardedRef,
    size: textSize[size],
    css: {
      ...merge(textCss[size], props.css)
    }
  });
});
Paragraph.displayName = "Paragraph";
var Paragraph_default = Paragraph;
var DEFAULT_TAG2 = "h1";
var Heading = React17.forwardRef((props, forwardedRef) => {
  const { size = "1", ...textProps } = props;
  const textSize = {
    1: { "@initial": "4", "@lg": "5" },
    2: { "@initial": "6", "@lg": "7" },
    3: { "@initial": "7", "@lg": "8" },
    4: { "@initial": "8", "@lg": "9" }
  };
  const textCss = {
    1: { fontWeight: 500, lineHeight: "20px", "@lg": { lineHeight: "23px" } },
    2: { fontWeight: 500, lineHeight: "25px", "@lg": { lineHeight: "30px" } },
    3: { fontWeight: 500, lineHeight: "33px", "@lg": { lineHeight: "41px" } },
    4: { fontWeight: 500, lineHeight: "35px", "@lg": { lineHeight: "55px" } }
  };
  return /* @__PURE__ */ React17.createElement(CoreText, {
    as: DEFAULT_TAG2,
    ...textProps,
    ref: forwardedRef,
    size: textSize[size],
    css: {
      fontVariantNumeric: "proportional-nums",
      ...merge(textCss[size], props.css)
    }
  });
});
Heading.displayName = "Heading";
var Heading_default = Heading;
var CoreInput = styled("input", {
  appearance: "none",
  borderWidth: "0",
  boxSizing: "border-box",
  fontFamily: "$input",
  margin: "0",
  outline: "none",
  padding: "0",
  width: "100%",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  backgroundColor: "$input-bg",
  boxShadow: "inset 0 0 0 1px $colors$input-shadow",
  color: "$input",
  fontVariantNumeric: "tabular-nums",
  "&:-webkit-autofill": {
    boxShadow: "inset 0 0 0 1px $colors$input-autofill-shadow, inset 0 0 0 100px $colors$input-autofill-shadow2"
  },
  "&:-webkit-autofill::first-line": {
    fontFamily: "$input",
    color: "$input"
  },
  "&:focus": {
    boxShadow: "inset 0px 0px 0px 1px $colors$input-focus-shadow, 0px 0px 0px 1px $colors$input-focus-shadow",
    "&:-webkit-autofill": {
      boxShadow: "inset 0px 0px 0px 1px $colors$input-autofill-focus-shadow, 0px 0px 0px 1px $colors$input-autofill-focus-shadow, inset 0 0 0 100px $colors$input-autofill-focus-shadow2"
    }
  },
  "&::placeholder": {
    color: "$input-placeholder"
  },
  "&:disabled": {
    pointerEvents: "none",
    backgroundColor: "$input-disabled-bg",
    color: "$input-disabled",
    cursor: "not-allowed",
    "&::placeholder": {
      color: "$input-disabled-placeholder"
    }
  },
  "&:read-only": {
    backgroundColor: "$input-read-only-bg",
    "&:focus": {
      boxShadow: "inset 0px 0px 0px 1px $colors$input-read-only-shadow"
    }
  },
  variants: {
    appearance: {
      "1": {
        borderRadius: "$1",
        height: "$5",
        fontSize: "$1",
        paddingLeft: "$1",
        paddingRight: "$1",
        lineHeight: "$sizes$5",
        "&:-webkit-autofill::first-line": {
          fontSize: "$1"
        }
      },
      "2": {
        borderRadius: "$2",
        height: "$6",
        fontSize: "$3",
        paddingLeft: "$2",
        paddingRight: "$2",
        lineHeight: "$sizes$6",
        "&:-webkit-autofill::first-line": {
          fontSize: "$3"
        }
      }
    },
    variant: {
      ghost: {
        boxShadow: "none",
        backgroundColor: "transparent",
        "@hover": {
          "&:hover": {
            boxShadow: "inset 0 0 0 1px $colors$input-ghost-focus-shadow"
          }
        },
        "&:focus": {
          backgroundColor: "$input-ghost-focus-bg",
          boxShadow: "inset 0px 0px 0px 1px $colors$input-ghost-focus-shadow, 0px 0px 0px 1px $colors$input-ghost-focus-shadow"
        },
        "&:disabled": {
          backgroundColor: "transparent"
        },
        "&:read-only": {
          backgroundColor: "transparent"
        }
      }
    },
    state: {
      invalid: {
        boxShadow: "inset 0 0 0 1px $colors$input-invalid-shadow",
        "&:focus": {
          boxShadow: "inset 0px 0px 0px 1px $colors$input-invalid-focus-shadow, 0px 0px 0px 1px $colors$input-invalid-focus-shadow"
        }
      },
      valid: {
        boxShadow: "inset 0 0 0 1px $colors$input-valid-shadow",
        "&:focus": {
          boxShadow: "inset 0px 0px 0px 1px $colors$input-valid-focus-shadow, 0px 0px 0px 1px $colors$input-valid-focus-shadow"
        }
      }
    },
    cursor: {
      default: {
        cursor: "default",
        "&:focus": {
          cursor: "text"
        }
      },
      text: {
        cursor: "text"
      }
    }
  },
  defaultVariants: {
    appearance: "1"
  }
});
var Input = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreInput, {
    ...props,
    ref: forwardedRef
  });
});
Input.displayName = "Input";
var Input_default = Input;
function LockJsx(props) {
  return /* @__PURE__ */ React17.createElement("svg", {
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    shapeRendering: "geometricPrecision",
    fill: "none",
    ...props
  }, /* @__PURE__ */ React17.createElement("path", {
    d: "M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"
  }), /* @__PURE__ */ React17.createElement("path", {
    d: "M1 1l22 22"
  }));
}
var StyledLock = styled(LockJsx);
var Lock = ({
  fill,
  ...props
}) => {
  return /* @__PURE__ */ React17.createElement(StyledLock, {
    css: {
      stroke: fill
    },
    ...props
  });
};
function UnLockJsx(props) {
  return /* @__PURE__ */ React17.createElement("svg", {
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    shapeRendering: "geometricPrecision",
    fill: "none",
    ...props
  }, /* @__PURE__ */ React17.createElement("path", {
    d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
  }), /* @__PURE__ */ React17.createElement("circle", {
    cx: "12",
    cy: "12",
    r: "3"
  }));
}
var StyledUnLock = styled(UnLockJsx);
var UnLock = ({
  fill,
  ...props
}) => {
  return /* @__PURE__ */ React17.createElement(StyledUnLock, {
    css: {
      stroke: fill
    },
    ...props
  });
};
var StyledLabel = styled(Label_default, {
  position: "absolute",
  textShadow: "none",
  lineHeight: "22px",
  paddingLeft: "$4",
  transformOrigin: "left top",
  transform: "scale(1) translate3d(0, 15px, 0)",
  transition: "200ms ease all",
  color: "$animated-input-label"
});
var StyledInput = styled("input", {
  appearance: "none",
  borderWidth: "0",
  boxSizing: "border-box",
  fontFamily: "$input",
  margin: "0",
  outline: "none",
  padding: "0",
  width: "100%",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  boxShadow: "none",
  backgroundColor: "transparent",
  color: "$animated-input",
  height: 52,
  lineHeight: "20px",
  paddingLeft: "$4",
  paddingRight: "$3",
  paddingTop: "$6",
  paddingBottom: "$2",
  "&:disabled": {
    backgroundColor: "transparent"
  },
  "&:read-only": {
    backgroundColor: "transparent"
  },
  "&:hover": {
    boxShadow: "none"
  },
  "&:focus": {
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:-webkit-autofill": {
      boxShadow: "none",
      "-webkit-box-shadow": "0 0 0px 1000px $colors$animated-input-webkit-autofill-focus inset",
      "-moz-box-shadow": "0 0 0px 1000px $colors$animated-input-webkit-autofill-focus inset"
    }
  },
  "&:-webkit-autofill": {
    boxShadow: "none",
    "-webkit-box-shadow": "0 0 0px 1000px $colors$animated-input-webkit-autofill inset",
    "-moz-box-shadow": "0 0 0px 1000px $colors$animated-input-webkit-autofill inset"
  }
});
var StyledInputWrapper = styled(Column_default, {
  boxShadow: "inset 0px 0px 0px 1px $colors$animated-input-wrapper-shadow",
  borderRadius: "$2",
  overflow: "hidden",
  position: "relative",
  backgroundColor: "$animated-input-wrapper-bg",
  border: "1px solid transparent",
  '&[data-focus="true"]': {
    border: "1px solid $colors$animated-input-wrapper-focus-border",
    backgroundColor: "$animated-input-wrapper-focus-bg"
  },
  '&[data-animate="true"]': {
    [`& ${StyledLabel}`]: {
      transform: "scale(0.8) translate3d(3px, 6px, 0)"
    }
  },
  '&[data-error="true"]': {
    border: "1px solid $color$animated-input-wrapper-error-border",
    [`& ${StyledLabel}`]: {
      color: "$animated-input-wrapper-error"
    }
  }
});
var AnimatedInput = React17.forwardRef(({
  name,
  type,
  label,
  value,
  showPassword,
  error,
  isFocused,
  canAnimate,
  rmClear,
  readOnly,
  autoFocus,
  autoComplete,
  onClear,
  onChange,
  onBlur,
  onFocus,
  toggleShowPassword
}, inputRef) => {
  return /* @__PURE__ */ React17.createElement(Column_default, {
    gap: "1",
    justify: "center",
    css: {
      display: type === "hidden" ? "none" : "flex"
    }
  }, /* @__PURE__ */ React17.createElement(StyledInputWrapper, {
    "data-focus": isFocused,
    "data-animate": canAnimate,
    "data-error": !!error,
    tabIndex: -1
  }, label ? /* @__PURE__ */ React17.createElement(StyledLabel, {
    htmlFor: name
  }, label) : null, /* @__PURE__ */ React17.createElement(Row_default, null, /* @__PURE__ */ React17.createElement(StyledInput, {
    key: name,
    id: name,
    "aria-invalid": error ? "true" : "false",
    type: type === "password" ? showPassword ? "text" : "password" : type,
    onChange,
    onBlur,
    onFocus,
    readOnly,
    value,
    name,
    ref: inputRef,
    autoFocus,
    autoComplete
  }), /* @__PURE__ */ React17.createElement(Flex_default, {
    align: "center",
    css: {
      position: "absolute",
      right: 0,
      bottom: 0,
      top: 0
    }
  }, type === "password" && /* @__PURE__ */ React17.createElement(Flex_default, {
    onClick: toggleShowPassword,
    align: "center",
    css: {
      padding: "$2",
      cursor: "pointer"
    }
  }, showPassword ? /* @__PURE__ */ React17.createElement(Lock, {
    fill: "$grey03",
    width: "1.15rem",
    height: "1.15rem"
  }) : /* @__PURE__ */ React17.createElement(UnLock, {
    fill: "$grey03",
    width: "1.15rem",
    height: "1.15rem"
  })), !rmClear && !readOnly && value !== void 0 && value !== "" ? /* @__PURE__ */ React17.createElement(Flex_default, {
    onClick: () => {
      onClear();
    },
    align: "center",
    css: {
      padding: "$2",
      paddingRight: "$4",
      cursor: "pointer"
    }
  }, /* @__PURE__ */ React17.createElement(CloseCircle, {
    fill: "$grey03",
    width: "1.15rem",
    height: "1.15rem"
  })) : null))), error ? /* @__PURE__ */ React17.createElement(Row_default, {
    css: {
      paddingLeft: "$1",
      paddingBottom: "$1"
    }
  }, error.type === "required" ? /* @__PURE__ */ React17.createElement(Text_default, {
    css: {
      color: "$animated-input-error"
    },
    role: "alert",
    size: "1"
  }, "This is required") : /* @__PURE__ */ React17.createElement(Text_default, {
    css: {
      color: "$animated-input-error"
    },
    role: "alert",
    size: "1"
  }, error.message)) : null);
});
AnimatedInput.displayName = "AnimatedInput";
var AnimatedInput_default = AnimatedInput;
var StyledTextAreaLabel = styled(Label_default, {
  position: "absolute",
  height: "100%",
  textShadow: "none",
  opacity: 0.75,
  paddingLeft: "$3",
  transformOrigin: "left top",
  transform: "scale(1) translate3d(0, 15px, 0)",
  transition: "200ms ease all",
  color: "$animated-textarea-label"
});
var StyledTextArea = styled("textarea", {
  appearance: "none",
  borderWidth: "0",
  boxSizing: "border-box",
  fontFamily: "$input",
  margin: "0",
  outline: "none",
  padding: "0",
  width: "100%",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  boxShadow: "none",
  backgroundColor: "transparent",
  height: 50,
  marginTop: 10,
  paddingLeft: "$3",
  paddingRight: "$3",
  paddingTop: "$2",
  paddingBottom: "$2",
  "&:disabled": {
    backgroundColor: "transparent"
  },
  "&:read-only": {
    backgroundColor: "transparent"
  },
  "&:hover": {
    boxShadow: "none"
  },
  "&:focus": {
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:-webkit-autofill": {
      boxShadow: "none",
      "-webkit-box-shadow": "0 0 0px 1000px $colors$animated-textarea-webkit-autofill-focus inset",
      "-moz-box-shadow": "0 0 0px 1000px $colors$animated-textarea-webkit-autofill-focus inset"
    }
  },
  "&:-webkit-autofill": {
    boxShadow: "none",
    "-webkit-box-shadow": "0 0 0px 1000px $colors$animated-textarea-webkit-autofill inset",
    "-moz-box-shadow": "0 0 0px 1000px $colors$animated-textarea-webkit-autofill inset"
  }
});
var StyledTextAreaWrapper = styled(Column_default, {
  boxShadow: "inset 0px 0px 0px 1px $colors$animated-textarea-wrapper-shadow",
  borderRadius: "$2",
  overflow: "hidden",
  position: "relative",
  backgroundColor: "$animated-textarea-wrapper-bg",
  '&[data-focus="true"]': {
    backgroundColor: "$input-ghost-focus-bg",
    boxShadow: "inset 0px 0px 0px 1px $colors$input-ghost-focus-shadow, 0px 0px 0px 1px $colors$input-ghost-focus-shadow"
  },
  '&[data-animate="true"]': {
    [`& ${StyledTextAreaLabel}`]: {
      opacity: 1,
      transform: "scale(0.8) translate3d(0, 5px, 0)"
    }
  },
  '&[data-error="true"]': {
    boxShadow: "inset 0px 0px 0px 1px $colors$animated-textarea-wrapper-error-border, 0px 0px 0px 1px $colors$animated-textarea-wrapper-error-border",
    [`& ${StyledTextAreaLabel}`]: {
      color: "$animated-textarea-wrapper-error"
    }
  }
});
var AnimatedTextarea = React17.forwardRef(({
  css,
  name,
  label,
  value,
  error,
  isFocused,
  canAnimate,
  readOnly,
  autoFocus,
  onClear,
  onChange,
  onBlur,
  onFocus
}, inputRef) => {
  return /* @__PURE__ */ React17.createElement(Column_default, {
    gap: "1",
    justify: "center"
  }, /* @__PURE__ */ React17.createElement(StyledTextAreaWrapper, {
    "data-focus": isFocused,
    "data-animate": canAnimate,
    "data-error": error,
    tabIndex: -1
  }, label ? /* @__PURE__ */ React17.createElement(StyledTextAreaLabel, {
    htmlFor: name
  }, label) : null, /* @__PURE__ */ React17.createElement(Row_default, null, /* @__PURE__ */ React17.createElement(StyledTextArea, {
    css,
    key: name,
    id: name,
    "aria-invalid": error ? "true" : "false",
    onChange,
    onBlur,
    onFocus,
    readOnly,
    maxLength: 500,
    value,
    name,
    ref: inputRef,
    autoFocus
  }), /* @__PURE__ */ React17.createElement(Flex_default, {
    align: "center",
    css: {
      position: "absolute",
      right: 0,
      bottom: 0,
      top: 0
    }
  }, !readOnly && value !== void 0 && value !== "" ? /* @__PURE__ */ React17.createElement(Flex_default, {
    onClick: () => {
      onClear();
    },
    align: "center",
    css: {
      padding: "$2",
      cursor: "pointer"
    }
  }, /* @__PURE__ */ React17.createElement(CloseCircle, {
    fill: "$grey03",
    width: "1.15rem",
    height: "1.15rem"
  })) : null))));
});
AnimatedTextarea.displayName = "AnimatedTextarea";
var AnimatedTextarea_default = AnimatedTextarea;
var StyledCheckbox = styled(CheckboxPrimitive.Root, {
  all: "unset",
  boxSizing: "border-box",
  userSelect: "none",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  alignItems: "center",
  appearance: "none",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: "1",
  margin: "0",
  outline: "none",
  padding: "0",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  color: "$checkbox",
  boxShadow: "inset 0 0 0 1px $colors$checkbox-shadow",
  overflow: "hidden",
  "@hover": {
    "&:hover": {
      boxShadow: "inset 0 0 0 1px $colors$checkbox-hover-shadow"
    }
  },
  "&:focus": {
    outline: "none",
    borderColor: "$checkbox-focus-border",
    boxShadow: "inset 0 0 0 1px $colors$checkbox-focus-shadow, 0 0 0 1px $colors$checkbox-focus-shadow"
  },
  variants: {
    size: {
      "1": {
        width: "$3",
        height: "$3",
        borderRadius: "$1"
      },
      "2": {
        width: "$4",
        height: "$4",
        borderRadius: "$1"
      },
      "3": {
        width: "$5",
        height: "$5",
        borderRadius: "$2"
      }
    }
  },
  defaultVariants: {
    size: "1"
  }
});
var StyledIndicator = styled(CheckboxPrimitive.Indicator, {
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
  width: "100%"
});
var Checkbox = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(StyledCheckbox, {
    ...props,
    ref: forwardedRef
  }, /* @__PURE__ */ React17.createElement(StyledIndicator, null, /* @__PURE__ */ React17.createElement(Checkmark, {
    fill: "currentColor"
  })));
});
Checkbox.displayName = "Checkbox";
var Checkbox_default = Checkbox;
var CoreWrapper = styled("div", {
  backgroundColor: "$select-bg",
  borderRadius: "$2",
  boxShadow: "inset 0 0 0 1px $colors$select-shadow",
  color: "$select",
  fontFamily: "$select",
  fontSize: "$1",
  fontVariantNumeric: "tabular-nums",
  fontWeight: 400,
  height: "$5",
  flexShrink: 0,
  "&:focus-within": {
    zIndex: 1,
    boxShadow: "inset 0px 0px 0px 1px $colors$select-focus-shadow, 0px 0px 0px 1px $colors$select-focus-shadow"
  }
});
var CoreSelect = styled("select", {
  appearance: "none",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: "inherit",
  color: "inherit",
  font: "inherit",
  outline: "none",
  width: "100%",
  height: "100%",
  paddingLeft: "$1",
  paddingRight: "$3",
  lineHeight: "25px"
});
var CaretSortIcon = styled(ChevronDown, {
  position: "absolute",
  pointerEvents: "none",
  display: "inline",
  marginTop: 5,
  marginLeft: -16
});
var Select = React17.forwardRef(({ css, ...props }, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreWrapper, {
    css
  }, /* @__PURE__ */ React17.createElement(CoreSelect, {
    ref: forwardedRef,
    ...props
  }), /* @__PURE__ */ React17.createElement(CaretSortIcon, null));
});
Select.toString = () => `.${CoreWrapper.className}`;
var Select_default = Select;

// src/Radio.tsx
var Radio_exports = {};
__export(Radio_exports, {
  Group: () => Group,
  Item: () => Item2
});
var Group = styled(RadioGroupPrimitive.Root, {
  display: "flex"
});
var CoreIndicator = styled(RadioGroupPrimitive.Indicator, {
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
  width: "100%",
  position: "relative",
  "&::after": {
    content: '""',
    display: "block",
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    backgroundColor: "$radio-indicator-bg"
  }
});
var CoreRadio = styled(RadioGroupPrimitive.Item, {
  all: "unset",
  boxSizing: "border-box",
  userSelect: "none",
  "&::before": {
    boxSizing: "border-box"
  },
  "&::after": {
    boxSizing: "border-box"
  },
  alignItems: "center",
  appearance: "none",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: "1",
  margin: "0",
  outline: "none",
  padding: "0",
  textDecoration: "none",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",
  borderRadius: "50%",
  color: "$radio",
  boxShadow: "inset 0 0 0 1px $colors$radio-shadow",
  overflow: "hidden",
  "@hover": {
    "&:hover": {
      boxShadow: "inset 0 0 0 1px $colors$radio-hover-shadow"
    }
  },
  "&:focus": {
    outline: "none",
    borderColor: "$radio-focus-border",
    boxShadow: "inset 0 0 0 1px $colors$radio-focus-shadow, 0 0 0 1px $colors$radio-focus-shadow"
  },
  variants: {
    size: {
      "1": {
        width: "$3",
        height: "$3"
      },
      "2": {
        width: "$5",
        height: "$5",
        [`& ${CoreIndicator}`]: {
          "&::after": {
            width: "$3",
            height: "$3"
          }
        }
      }
    }
  },
  defaultVariants: {
    size: "1"
  }
});
var Item2 = React17.forwardRef((props, forwardedRef) => {
  return /* @__PURE__ */ React17.createElement(CoreRadio, {
    ...props,
    ref: forwardedRef
  }, /* @__PURE__ */ React17.createElement(CoreIndicator, null));
});
Item2.displayName = "RadioItem";

export { AnimatedInput_default as AnimatedInput, AnimatedTextarea_default as AnimatedTextarea, Box_default as Box, Button_default as Button, Checkbox_default as Checkbox, Column_default as Column, Divider_default as Divider, Flex_default as Flex, Grid_default as Grid, Heading_default as Heading, IconButton_default as IconButton, Input_default as Input, Label_default as Label, Paragraph_default as Paragraph, Radio_exports as Radio, Row_default as Row, Select_default as Select, Skeleton_default as Skeleton, Stack_default as Stack, StyledInput, StyledInputWrapper, StyledLabel, StyledTextArea, StyledTextAreaLabel, StyledTextAreaWrapper, Text_default as Text, TextArea_default as TextArea };
